import React, { useState, createContext, useEffect, Dispatch, SetStateAction } from "react";
import SurveyQuestionDatePicker from "./questions/survey_question_date_picker";
import MentorshipChatGroupStudentSelect from "./questions/mentorship_chat_group/student_select";
import MentorshipChatGroupOptionalStudentInfo from "./questions/mentorship_chat_group/optional_student_info";
import MultiSelect from "./questions/multi_select";
import { gql } from "@urql/core";
import {
  SurveyDocument,
  SaveSurveyResponseDocument,
  SurveyQuery,
  Survey as SurveyType, // not great, but there's a conflicting declaration of the Survey class in this file
  SurveyType as SurveyTypeType, // not great, but there's a conflicting declaration of the Survey class in this file
  SurveyAnswerInput,
  SurveyQuestionTemplateId,
  SurveyQuestion,
  SaveSurveyResponseMutationVariables,
} from "../generated/graphql";
import Conversation from "./questions/conversation";
import Flags from "./questions/flags";
import Nps from "./questions/nps";
import SurveyQuestionSelect from "./questions/select/survey_question_select";
import SurveyQuestionLikert from "./questions/likert/survey_question_likert";
import RotatingLikert from "./questions/rotating_likert";
import RotatingOpenResponse from "./questions/rotating_open_response";
import RotatingCareerOpenResponse from "./questions/rotating_career_open_response";
import SurveyQuestionSelectWithSelectFollowUp from "./questions/survey_question_select_with_select_follow_up";
import Generic from "./questions/generic/generic";
import styled from "styled-components";
import CounterpartsInfo from "./questions/counterparts_info";
import SurveyQuestionNumberInput from "./questions/survey_question_number_input";
import { useQuery, useMutation } from "urql";
import SurveyQuestionOpenResponse from "./questions/survey_question_open_response";
import SurveyQuestionTextInput from "./questions/survey_question_text_input";
import DropdownComponent from "./questions/multi_select_dropdown";
import useDeviceType from "../shared/use_device_type";
import { captureError } from "../../utils/capture_error";

export interface QuestionProps<Answer, Customizations> {
  customizations: Customizations;
  currentAnswer?: Answer;
  updateAnswer: (newAnswer: Answer) => void;
  setAbleToSubmit: (ableToSubmit: boolean) => void;
  questionId?: string | number;
  maxValue?: number;
  showValidation: boolean;
  showRefreshedComponents?: boolean;
  hackToHideFeedback?: boolean;
  dataTest?: string;
}

function Question({
  answer,
  templateId,
  customizations,
  updateAnswer,
  setAbleToSubmit,
  qid,
  questionId,
  showValidation,
  showRefreshedComponents,
}: {
  answer?: SurveyAnswerInput["answerJson"];
  templateId: SurveyQuestionTemplateId;
  customizations: SurveyQuestion["customizations"];
  updateAnswer: (newAnswer: SurveyAnswerInput["answerJson"]) => void;
  setAbleToSubmit: (ableToSubmit: boolean) => void;
  qid?: string | null;
  questionId: number | string;
  showValidation: boolean;
  showRefreshedComponents: boolean;
}) {
  switch (templateId) {
    case SurveyQuestionTemplateId.DatePicker:
      return (
        <SurveyQuestionDatePicker
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
          showRefreshedComponents={showRefreshedComponents}
        />
      );
    case SurveyQuestionTemplateId.OpenResponse:
      if (qid === "signature" && showRefreshedComponents) {
        return (
          <SurveyQuestionTextInput
            currentAnswer={answer}
            customizations={customizations}
            updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
            setAbleToSubmit={setAbleToSubmit}
            questionId={questionId}
            showValidation={showValidation}
          />
        );
      }

      return (
        <SurveyQuestionOpenResponse
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
          showRefreshedComponents={showRefreshedComponents}
          // This hack is in place because the open-response question on the Post-Session Survey
          // leads to some answers from participants where participants/partners expect quick action.
          // However, there is no good process for reviewing PSS responses in a timely manner.
          // It takes PS a lot of manual time to review these responses via exports.
          // Removing a question from the PSS "the right way" is high-effort due to current survey design.
          // Additionally, the initial ask was to include a text-only (no-input) question
          // to redirect participants to our normal helpdesk flow.
          // This hack is acceptable from a product perspective so that we don't have the liability of
          // having to do something with feedback that we cannot quickly and efficiently address.
          // Ideally the entire PSS is replaced by a different solution, since there are multiple
          // issues with the PSS as a whole.
          hackToHideFeedback={[29, 35, 40, 46, 82, 382, 387, 6179, 6187].includes(
            parseInt(questionId.toString()),
          )}
        />
      );
    case SurveyQuestionTemplateId.MentorshipChatGroupStudentSelect:
      return (
        <MentorshipChatGroupStudentSelect
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
        />
      );
    case SurveyQuestionTemplateId.MentorshipChatGroupOptionalStudentInfo:
      return (
        <MentorshipChatGroupOptionalStudentInfo
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
        />
      );
    // Note that even though this question template is called "MultiSelect", currently we don't have any survey
    // question that requires selecting multiple options. There is a property called `numberToChoose` in the customizations json
    // that indicates how many options can be selected, but it is always set to 1 (in other words, it's a single-select question).
    // Probably would be a good idea to refactor/rename this to avoid confusion.
    case SurveyQuestionTemplateId.MultiSelect:
      return (
        <MultiSelect
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
          showRefreshedComponents={showRefreshedComponents}
        />
      );
    case SurveyQuestionTemplateId.Conversation:
      return (
        <Conversation
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
        />
      );
    case SurveyQuestionTemplateId.Flags:
      return (
        <Flags
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
        />
      );
    case SurveyQuestionTemplateId.Nps:
      return (
        <Nps
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
        />
      );
    case SurveyQuestionTemplateId.Likert:
      return (
        <SurveyQuestionLikert
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
          showRefreshedComponents={showRefreshedComponents}
        />
      );
    case SurveyQuestionTemplateId.Select:
      return (
        <SurveyQuestionSelect
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
          showRefreshedComponents={showRefreshedComponents}
        />
      );
    case SurveyQuestionTemplateId.RotatingLikert:
      return (
        <RotatingLikert
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
        />
      );
    case SurveyQuestionTemplateId.RotatingOpenResponse:
      return (
        <RotatingOpenResponse
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
        />
      );
    case SurveyQuestionTemplateId.RotatingCareerOpenResponse:
      return (
        <RotatingCareerOpenResponse
          customizations={customizations}
          currentAnswer={answer}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
        />
      );
    case SurveyQuestionTemplateId.SelectWithSelectFollowUp:
      return (
        <SurveyQuestionSelectWithSelectFollowUp
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
          showRefreshedComponents={showRefreshedComponents}
        />
      );
    case SurveyQuestionTemplateId.CounterpartsInfo:
      return (
        <CounterpartsInfo
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
        />
      );
    case SurveyQuestionTemplateId.MultiSelectDropdown:
      return (
        <DropdownComponent
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
          showRefreshedComponents={showRefreshedComponents}
        />
      );
    case SurveyQuestionTemplateId.Generic:
      return (
        <Generic
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
          showRefreshedComponents={showRefreshedComponents}
        />
      );
    case SurveyQuestionTemplateId.NumberInput:
      return (
        <SurveyQuestionNumberInput
          currentAnswer={answer}
          customizations={customizations}
          updateAnswer={(newAnswer) => updateAnswer(newAnswer)}
          setAbleToSubmit={setAbleToSubmit}
          questionId={questionId}
          showValidation={showValidation}
          showRefreshedComponents={showRefreshedComponents}
        />
      );
    default:
      throw new Error(`Couldn't find survey question template ${templateId}`);
  }
}

export enum SurveyDisplay {
  page,
  banner,
}

export const SurveyContext = createContext<{
  surveyId: string;
  mentorshipChatGroupId?: string;
  mentorshipId?: string;
  participantId: string;
  display: SurveyDisplay;
  programId?: string;
}>({ participantId: "", surveyId: "", display: SurveyDisplay.page, programId: "" });

gql`
  query Survey(
    $id: ID!
    $participantId: ID
    $surveyCustomRenderOverride: SurveyCustomRenderOverride
    $questionFilter: SurveyQuestionFilter
  ) {
    survey(id: $id) {
      id
      name
      description(
        participantId: $participantId
        surveyCustomRenderOverride: $surveyCustomRenderOverride
      )
      surveyType
      questions(filter: $questionFilter, surveyCustomRenderOverride: $surveyCustomRenderOverride) {
        id
        qid
        index
        template {
          id
          templateId
        }
        customizations
        createdInAdminTool
      }
    }
  }
`;

// in addition to the survey response, we also need to query for everything that might have changed because of
// a side effect to update the cache. for now this just includes conversation logs and # of survey responses
gql`
  mutation SaveSurveyResponse($inputResponse: SurveyResponseInput!) {
    saveSurveyResponse(response: $inputResponse) {
      errors
      response {
        id
        context {
          participantId
          mentorshipId
          mentorshipChatGroupId
        }
      }
      user {
        id
        participants {
          id
          mentorships {
            id
            studentIsDisengaged
            mentorIsDisengaged
            mentorConversationLogs {
              id
            }
            studentConversationLogs {
              id
            }
          }
          surveyResponses {
            id
            survey {
              id
            }
          }
        }
        # To properly update the urql cache, we need to re-fetch all surveyResponses for this participant.
        # We can't just fetch by id, the cache will not update.
        surveyResponses {
          id
          survey {
            id
          }
        }
      }
    }
  }
`;

const StyledSurvey = styled.div`
  width: 100%;

  .showRefreshedComponents {
    margin: 0;
  }

  .question {
    display: flex;
    flex-direction: column;
    padding: ${({ theme }) => theme.sizing.scale300};
  }

  .mobile {
    padding: ${({ theme }) => theme.sizing.scale300};
  }
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.font.size.header.four};
`;

interface CustomRenderOverride {
  userId: string;
  programId: string;
}

export interface SurveyState {
  survey: SurveyType;
  answers: { [questionId: string]: SurveyAnswerInput["answerJson"] };
  savingResponse: boolean;
  ableToSubmit: boolean;
  fetching: boolean;
  setAnswers: (args: { [questionId: string]: SurveyAnswerInput["answerJson"] }) => void;
  setSavingResponse: (args: boolean) => void;
  saveSurveyResponse: (args: SaveSurveyResponseMutationVariables) => void;
  setIdsOfQuestionsAbleToSubmit: Dispatch<SetStateAction<string[]>>;
  questionsWithErrors: SurveyType["questions"];
  showValidation: boolean;
  setShowValidation: (showValidation: boolean) => void;
}

export function useSurveyState({
  id,
  userId,
  onSubmit,
  participantId = "",
  surveyCustomRenderOverride,
}: {
  id: string;
  userId: string;
  onSubmit: () => void;
  participantId?: string;
  surveyCustomRenderOverride?: CustomRenderOverride;
}): SurveyState {
  const [answers, setAnswers] = useState<{
    [questionId: string]: SurveyAnswerInput["answerJson"];
  }>({});
  const [idsOfQuestionsAbleToSubmit, setIdsOfQuestionsAbleToSubmit] = useState<string[]>([]);
  const [savingResponse, setSavingResponse] = useState<boolean>(false);
  const [saveResponseResult, saveSurveyResponse] = useMutation(SaveSurveyResponseDocument);
  const [showValidation, setShowValidation] = useState(false);

  const [{ fetching, error, data }] = useQuery({
    query: SurveyDocument,
    variables: {
      id: id,
      participantId: participantId, // needed to transform variables in Description field
      surveyCustomRenderOverride: surveyCustomRenderOverride,
      questionFilter: {
        userId: userId,
        participantId: participantId,
      },
    },
    pause: !id,
  });

  useEffect(() => {
    if (saveResponseResult.data?.saveSurveyResponse) {
      const response = saveResponseResult.data.saveSurveyResponse;
      if (response.errors?.length) {
        throw new Error(`Could not save survey: ${response.errors.join(", ")}`);
      } else {
        onSubmit();
      }
    }
  }, [onSubmit, saveResponseResult.data?.saveSurveyResponse]);

  if (fetching || data === null || !id) {
    return {
      fetching: true,
      survey: {} as SurveyType,
      ableToSubmit: false,
      answers,
      setAnswers,
      saveSurveyResponse,
      setSavingResponse,
      savingResponse,
      setIdsOfQuestionsAbleToSubmit,
      questionsWithErrors: [],
      showValidation,
      setShowValidation,
    };
  }

  if (error || !data || saveResponseResult.error) {
    if (error || !data) {
      const graphQLErrorsForAPM = error?.graphQLErrors?.join("; ") ?? "";
      captureError(new Error(`Error fetching useSurveyQuery: ${graphQLErrorsForAPM}`));
    }

    throw new Error("Uh oh");
  }

  const survey = data.survey;
  const questionsWithErrors = survey.questions.filter(
    (question: SurveyQuery["survey"]["questions"][0]) =>
      !idsOfQuestionsAbleToSubmit.includes(question.id),
  );

  const ableToSubmit = survey.questions.reduce(
    (ableToSubmit: boolean, question: SurveyQuery["survey"]["questions"][0]) =>
      idsOfQuestionsAbleToSubmit.includes(question.id) ? ableToSubmit : false,
    true,
  );

  return {
    fetching,
    survey,
    answers,
    setAnswers,
    saveSurveyResponse,
    setSavingResponse,
    savingResponse,
    setIdsOfQuestionsAbleToSubmit,
    ableToSubmit,
    questionsWithErrors,
    showValidation,
    setShowValidation,
  };
}

export default function Survey({
  survey,
  context,
  className,
  savingResponse,
  answers,
  setAnswers,
  setIdsOfQuestionsAbleToSubmit,
  display = SurveyDisplay.page,
  showValidation = false,
}: {
  survey: SurveyType;
  // @TODO: might need to come back and re-think this a bit. What other contexts could a survey be used for?
  context?: {
    mentorshipChatGroupId?: string;
    mentorshipId?: string;
    participantId?: string;
    programId?: string;
  };
  className?: string;
  savingResponse: boolean;
  answers: { [questionId: string]: SurveyAnswerInput["answerJson"] };
  setAnswers: (args: { [questionId: string]: SurveyAnswerInput["answerJson"] }) => void;
  setIdsOfQuestionsAbleToSubmit: Dispatch<SetStateAction<string[]>>;
  display?: SurveyDisplay;
  showValidation?: boolean;
}) {
  const { isMobile } = useDeviceType();
  const QUICK_CONVERSATION_LOGGING_SURVEY_ID = "28";

  // TA-879: due to time and resource constraints, these surveys will not have a refreshed UI
  const showRefreshedComponents =
    survey.surveyType !== SurveyTypeType.PostSession &&
    survey.id !== QUICK_CONVERSATION_LOGGING_SURVEY_ID;

  return (
    <StyledSurvey
      className={`${className} ${showRefreshedComponents ? "showRefreshedComponents" : ""}`}
      data-test="survey-question-accessibility"
    >
      {survey.description && (
        <Description dangerouslySetInnerHTML={{ __html: survey.description }}></Description>
      )}
      {/* Hide this notice on survey banners, which are limited to one question. */}
      {!showRefreshedComponents && display !== SurveyDisplay.banner && (
        <p>NOTE: Fields marked with an asterisk (*) are required.</p>
      )}
      {!savingResponse ? (
        <SurveyContext.Provider
          value={{ participantId: "", surveyId: survey.id, display, ...context }}
        >
          {survey.questions
            .sort((question1, question2) => (question1.index < question2.index ? -1 : 1))
            .map((question) => {
              return (
                <div
                  className={`question ${isMobile ? "mobile" : ""}`}
                  key={question.id}
                  id={question.id}
                >
                  <Question
                    showRefreshedComponents={showRefreshedComponents}
                    answer={answers[question.id]}
                    qid={question.qid}
                    questionId={question.id}
                    customizations={question.customizations}
                    templateId={question.template.templateId}
                    showValidation={showValidation}
                    updateAnswer={(answer: string) =>
                      setAnswers({ ...answers, [question.id]: answer })
                    }
                    setAbleToSubmit={(ableToSubmit: boolean) => {
                      // we use an updater function here since we're relying on previous state, and
                      // there could be multiple questions updating at the same time
                      // https://reactjs.org/docs/hooks-reference.html#functional-updates
                      setIdsOfQuestionsAbleToSubmit((currentIds) => {
                        if (ableToSubmit) {
                          if (!currentIds.includes(question.id)) {
                            return [...currentIds, question.id];
                          }
                        } else {
                          if (currentIds.includes(question.id)) {
                            return currentIds.filter((questionId) => questionId !== question.id);
                          }
                        }
                        return currentIds;
                      });
                    }}
                  />
                </div>
              );
            })}
        </SurveyContext.Provider>
      ) : (
        "Saving..."
      )}
    </StyledSurvey>
  );
}
